import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, EmptyState, VStack, Spinner } from "@chakra-ui/react";
import { HiColorSwatch } from "react-icons/hi";
import { useEffect, useRef, useState } from "react";
import {
  rxAssistantService,
  type SessionSummary,
} from "../../services/rxAssistantService";
import { FiTrash2, FiChevronRight } from "react-icons/fi";
import { toaster } from "../../components/ui/toaster";

export const ChatHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(
    null,
  );
  const hasFetchedRef = useRef(false);

  const tabs = [
    {
      label: "Chat",
      path: "/ai-assistant/chat",
    },
    {
      label: "History",
      path: "/ai-assistant/history",
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchSessions();
      hasFetchedRef.current = true;
    }
  }, []);

  const fetchSessions = async () => {
    try {
      setIsLoading(true);
      const response = await rxAssistantService.getSessions();
      setSessions(response.sessions);
    } catch (error: any) {
      toaster.create({
        title: "Error",
        description: "Failed to fetch chat history",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSession = async (
    sessionId: number,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    if (!confirm("Are you sure you want to delete this conversation?")) {
      return;
    }

    try {
      setDeletingSessionId(sessionId);
      await rxAssistantService.deleteSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      toaster.create({
        title: "Success",
        description: "Conversation deleted successfully",
        type: "success",
      });
    } catch (error: any) {
      toaster.create({
        title: "Error",
        description: "Failed to delete conversation",
        type: "error",
      });
    } finally {
      setDeletingSessionId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const dateStr = date.toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    return { time: timeStr, date: dateStr };
  };

  return (
    <div className="max-w-5xl mx-auto p-3!">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl! font-medium! text-[#302E2E]">RX Assistant</h1>

        <nav
          className="flex gap-1 rounded-lg bg-gray-100 p-1 w-fit"
          aria-label="Tabs">
          {tabs.map((tab) => {
            const active = isActive(tab.path);

            return (
              <Link
                key={tab.label}
                to={tab.path}
                className={`px-4! py-2! text-base! font-medium! rounded-lg transition-all duration-200 ${
                  active
                    ? "text-[#0142B1]! bg-white!"
                    : "text-[#302E2E] hover:bg-gray-200!"
                }`}>
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* History Section */}
      <div>
        <h2 className="text-xl! font-medium! mb-4">History</h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : sessions.length === 0 ? (
          <EmptyState.Root>
            <EmptyState.Content>
              <EmptyState.Indicator>
                <HiColorSwatch />
              </EmptyState.Indicator>
              <VStack textAlign="center">
                <EmptyState.Title>Start a new conversation</EmptyState.Title>
                <EmptyState.Description>
                  Start a new conversation with RX Assistant
                </EmptyState.Description>
              </VStack>
              <Button
                onClick={() => navigate("/ai-assistant/chat")}
                bgColor={"blue.700"}>
                Chat with RX Assistant
              </Button>
            </EmptyState.Content>
          </EmptyState.Root>
        ) : (
          <div className="flex flex-col gap-2">
            {sessions.map((session) => {
              const { time, date } = formatDate(session.created_at);
              return (
                <div
                  key={session.id}
                  className="flex items-center h-[60px] bg-white border-b border-[#DADADA] px-5 cursor-pointer hover:bg-gray-50 transition-colors group relative"
                  onClick={() =>
                    navigate(`/ai-assistant/session/${session.id}`)
                  }>
                  {/* AI Icon */}
                  <div className="flex items-center gap-2 flex-1">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="shrink-0">
                      <circle
                        cx="4"
                        cy="4"
                        r="2"
                        stroke="#0142B1"
                        strokeWidth="1"
                        fill="none"
                      />
                      <circle
                        cx="12"
                        cy="8"
                        r="2"
                        stroke="#0142B1"
                        strokeWidth="1"
                        fill="none"
                      />
                      <line
                        x1="6"
                        y1="4"
                        x2="10"
                        y2="8"
                        stroke="#0142B1"
                        strokeWidth="1"
                      />
                    </svg>

                    {/* Title and timestamp */}
                    <div className="flex flex-col gap-1">
                      <p className="text-sm! font-medium! leading-[130%]! text-black truncate max-w-[500px] text-wrap text-ellipsis">
                        {session.title}
                      </p>
                      <div className="flex items-center gap-1 text-xs! font-normal! text-[#605F5F]">
                        <span>{time}</span>
                        <span>|</span>
                        <span>{date}</span>
                      </div>
                    </div>
                  </div>

                  {/* Chevron */}
                  <FiChevronRight
                    className="text-[#302E2E] shrink-0"
                    size={16}
                  />

                  {/* Delete button (on hover) */}
                  <button
                    onClick={(e) => handleDeleteSession(session.id, e)}
                    disabled={deletingSessionId === session.id}
                    className="absolute right-12 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1.5 rounded-md hover:bg-red-50 transition-all disabled:opacity-50">
                    {deletingSessionId === session.id ? (
                      <Spinner size="sm" />
                    ) : (
                      <FiTrash2 size={14} />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
